import {inject, Pipe, PipeTransform} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

@Pipe({
  name: 'relativeTime',
  standalone: false,
})
export class RelativeTimePipe implements PipeTransform {
  private readonly translate = inject(TranslateService);

  transform(value: Date | string | number): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (typeof value === 'string') {
      const monthlyResult = this.handleMonthlyValue(value);
      if (monthlyResult !== null) {
        return monthlyResult;
      }
    }

    const date = this.parseToDate(value);
    if (!date) {
      return String(value);
    }

    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const ago = this.translate.instant('common.time.ago');

    if (seconds < 60) {
      return this.translate.instant('common.time.justNow');
    }

    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const unit =
        minutes === 1
          ? this.translate.instant('common.time.minute')
          : this.translate.instant('common.time.minutes');
      return `${minutes} ${unit} ${ago}`;
    }

    if (seconds < 86400) {
      const hours = Math.floor(seconds / 3600);
      const unit =
        hours === 1
          ? this.translate.instant('common.time.hour')
          : this.translate.instant('common.time.hours');
      return `${hours} ${unit} ${ago}`;
    }

    const days = Math.floor(seconds / 86400);
    const unit =
      days === 1
        ? this.translate.instant('common.time.day')
        : this.translate.instant('common.time.days');
    return `${days} ${unit} ${ago}`;
  }

  private handleMonthlyValue(originalValue: string): string | null {
    const parsed = this.parseMonthInput(originalValue);
    if (!parsed) {
      return null;
    }

    // Only treat as monthly when the provided day is the first of the month
    if (parsed.day !== 1) {
      return null;
    }

    const now = new Date();
    const monthDiff =
      (now.getFullYear() - parsed.date.getFullYear()) * 12 +
      (now.getMonth() - parsed.date.getMonth());

    if (monthDiff < 0) {
      return parsed.original; // future date, return as is
    }

    const monthKeys: Record<number, string> = {
      0: 'thisMonth',
      1: 'oneMonthAgo',
      2: 'twoMonthsAgo',
      3: 'threeMonthsAgo',
    };

    if (monthDiff in monthKeys) {
      return this.translate.instant(`common.time.${monthKeys[monthDiff]}`);
    }

    return parsed.original;
  }

  private parseMonthInput(
    value: string,
  ): {date: Date; original: string; day: number} | null {
    const trimmed = value.trim();
    const normalized = trimmed.replace(/\//g, '-');
    if (!normalized) {
      return null;
    }

    const parts = normalized.split('-');
    let year: number | undefined;
    let month: number | undefined;
    let day = 1;

    if (parts.length === 2) {
      const [first, second] = parts;
      if (first.length === 4) {
        year = Number(first);
        month = Number(second);
      } else if (second.length === 4) {
        month = Number(first);
        year = Number(second);
      }
    } else if (parts.length === 3) {
      const [first, second, third] = parts;

      if (first.length === 4) {
        year = Number(first);
        let possibleDay = Number(second);
        let possibleMonth = Number(third);

        if (possibleMonth > 12 && possibleDay <= 12) {
          [possibleDay, possibleMonth] = [possibleMonth, possibleDay];
        }

        month = Number(second) <= 12 ? Number(second) : possibleMonth;
        day = Number(second) <= 12 ? Number(third) : possibleDay;
      } else if (third.length === 4) {
        year = Number(third);
        month = Number(second);
        day = Number(first);
      }
    }

    if (
      year === undefined ||
      month === undefined ||
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      month < 1 ||
      month > 12
    ) {
      return null;
    }

    const parsedDate = new Date(year, month - 1, day);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return {date: parsedDate, original: trimmed, day};
  }

  private parseToDate(value: Date | string | number): Date | null {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'number') {
      const dateFromNumber = new Date(value);
      return Number.isNaN(dateFromNumber.getTime()) ? null : dateFromNumber;
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }

    const reordered = this.parseMonthInput(value);
    return reordered ? reordered.date : null;
  }
}
